// Career meta-layer: persists ABOVE the club. Survives resetGame() and
// "change club" (which only resets the gameStore). Separate persist key,
// same pattern as achievementsStore.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { LeagueHistoryEntry } from '@/config/leagueConfig'
import type { ClubOffer } from '@/config/careerConfig'
import { BOARD_CONFIG } from '@/config/careerConfig'

export interface CareerClubRecord {
  clubName: string
  seasonsManaged: number
  titles: number
  promotions: number
  relegations: number
  bestDivision: number // lowest division number reached (1 = top)
  endedReason: 'left' | 'fired' | 'victory'
  leagueHistory: LeagueHistoryEntry[]
}

interface CareerState {
  managerReputation: number // 0-100, separate from club reputation
  currentClubName: string
  clubHistory: CareerClubRecord[]
  pendingOffers: ClubOffer[]

  addManagerReputation: (delta: number) => void
  setCurrentClubName: (name: string) => void
  archiveCurrentClub: (record: CareerClubRecord) => void
  setPendingOffers: (offers: ClubOffer[]) => void
  acceptOffer: (offerId: string) => ClubOffer | null
  clearOffers: () => void
  reset: () => void
}

const initialCareer = {
  managerReputation: 0,
  currentClubName: '',
  clubHistory: [] as CareerClubRecord[],
  pendingOffers: [] as ClubOffer[],
}

export const useCareerStore = create<CareerState>()(
  persist(
    (set, get) => ({
      ...initialCareer,

      addManagerReputation: (delta) =>
        set({ managerReputation: Math.max(0, Math.min(100, get().managerReputation + delta)) }),

      setCurrentClubName: (name) => set({ currentClubName: name }),

      archiveCurrentClub: (record) =>
        set({ clubHistory: [...get().clubHistory, record] }),

      setPendingOffers: (offers) => set({ pendingOffers: offers }),

      acceptOffer: (offerId) => {
        const offer = get().pendingOffers.find(o => o.id === offerId) ?? null
        if (offer) set({ pendingOffers: [] })
        return offer
      },

      clearOffers: () => set({ pendingOffers: [] }),

      reset: () => set({ ...initialCareer }),
    }),
    {
      name: 'padel-manager-career',
      version: 1,
    }
  )
)

export { BOARD_CONFIG }
